import React from 'react';
import { PRESENCE_DOT_COLORS, PRESENCE_STATUS } from '../utils/dashboard/constants';

function presenceDotColor(status) {
  if (status === PRESENCE_STATUS.ONLINE || status === 'online') {
    return PRESENCE_DOT_COLORS.online || '#22c55e';
  }
  if (status === PRESENCE_STATUS.TEMP_OFFLINE || status === 'temp_offline') {
    return PRESENCE_DOT_COLORS.temp_offline || '#f59e0b';
  }
  return PRESENCE_DOT_COLORS.offline || '#9ca3af';
}

function TrackSwitch({ on, onToggle, title }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      title={title || (on ? '关闭轨迹绘制' : '打开轨迹绘制')}
      onClick={(e) => {
        e.stopPropagation();
        onToggle?.(!on);
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        flexShrink: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        padding: '0 2px',
        fontSize: 11,
        color: on ? '#2e7d32' : '#8a93a0',
        fontWeight: on ? 600 : 400,
        lineHeight: 1
      }}
    >
      <span>轨迹</span>
      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          width: 28,
          height: 16,
          borderRadius: 8,
          background: on ? '#2e7d32' : '#cfd5de',
          transition: 'background 0.15s ease',
          flexShrink: 0
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: on ? 14 : 2,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
            transition: 'left 0.15s ease'
          }}
        />
      </span>
    </button>
  );
}

function LocatePinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 22s7-7.2 7-12.2A7 7 0 1 0 5 9.8C5 14.8 12 22 12 22z"
        stroke="#2563eb"
        strokeWidth="1.8"
        fill="#dbeafe"
      />
      <circle cx="12" cy="9.5" r="2.2" fill="#2563eb" />
    </svg>
  );
}

function CameraIcon({ active }) {
  const stroke = active ? '#e53935' : '#9ca3af';
  const fill = active ? '#fee2e2' : '#f3f4f6';
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="7" width="13" height="11" rx="2" stroke={stroke} strokeWidth="1.7" fill={fill} />
      <path d="M16 10.5l5-2.5v9l-5-2.5v-4z" stroke={stroke} strokeWidth="1.7" fill={fill} strokeLinejoin="round" />
    </svg>
  );
}

/**
 * 全部队员 / 任务中 共用两行收起态（对齐截图）。
 * 点名字不飞点；点右侧定位图标飞点；最右侧细线箭头收起向右、展开向下。
 */
export default function MemberCollapsedCard({
  displayName,
  presenceStatus = PRESENCE_STATUS.OFFLINE,
  expanded = false,
  onToggleExpand = null,
  nameTitle = null,
  onNameClick = null,
  nameRef = null,
  showVoice = false,
  voiceNode = null,
  freqBadge = null,
  missionTag = null,
  onLocate = null,
  isStreaming = false,
  onOpenLive = null,
  statusText = null,
  batteryText = null,
  accuracyText = null,
  trackOn = false,
  onTrackToggle = null,
  trackTitle = null,
  /** 收起态默认不显示轨迹滑块（截图第二行仅时间·电量）；展开底栏自行放轨迹 */
  showCollapsedTrackSwitch = false,
  rightExtra = null,
  draggable = false,
  onDragStart = null,
  rowStyle = null
}) {
  const online = presenceStatus === PRESENCE_STATUS.ONLINE || presenceStatus === 'online';
  const rowBg = expanded ? '#eef5ff' : '#fff';
  const borderColor = expanded ? '#93c5fd' : '#e5e7eb';

  const metaParts = [statusText, batteryText].filter(Boolean);
  const metaLine = metaParts.join(' · ');
  const metaTitle = [statusText, batteryText, accuracyText].filter(Boolean).join(' · ');

  return (
    <div
      className={expanded ? 'mission-member-card is-expanded' : 'mission-member-card'}
      draggable={draggable}
      onDragStart={onDragStart}
      style={{
        padding: '8px 10px',
        background: rowBg,
        border: `1px solid ${borderColor}`,
        borderRadius: 8,
        userSelect: 'none',
        boxShadow: expanded ? '0 0 0 1px rgba(59,130,246,0.12)' : 'none',
        ...(rowStyle || {})
      }}
    >
      {/* 第一行：点 · 名 · 麦 · 徽标 | 任务pill · 定位 · 直播 · ▲/▼ */}
      <div className="mission-member-first-line" style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 24 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: presenceDotColor(presenceStatus),
            flexShrink: 0
          }}
          title={online ? '在线' : (presenceStatus === PRESENCE_STATUS.TEMP_OFFLINE || presenceStatus === 'temp_offline' ? '暂时离开' : '离线')}
        />

        <span
          ref={nameRef} className="mission-member-name"
          role={onNameClick ? 'button' : undefined}
          title={nameTitle || displayName}
          onClick={(e) => {
            e.stopPropagation();
            onNameClick?.(e);
          }}
          style={{
            fontWeight: 700,
            flex: '0 1 auto',
            minWidth: 0,
            maxWidth: missionTag ? '28%' : '36%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#111827',
            fontSize: 13,
            cursor: onNameClick ? 'pointer' : 'default'
          }}
        >
          {displayName}
        </span>

        {showVoice && voiceNode ? (
          <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', opacity: online ? 1 : 0.55 }}>
            {voiceNode}
          </span>
        ) : null}

        {freqBadge}

        <div
          className="mission-member-trailing"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 6,
            flex: '1 1 auto',
            minWidth: 0,
            marginLeft: 'auto'
          }}
        >
          {missionTag ? (
            <span
              className="mission-member-task-tag" title={`任务：${missionTag}`}
              style={{
                maxWidth: 84,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 11,
                fontWeight: 600,
                color: '#1d4ed8',
                background: '#dbeafe',
                borderRadius: 999,
                padding: '2px 8px',
                flexShrink: 1
              }}
            >
              {missionTag}
            </span>
          ) : null}

          {onLocate ? (
            <button
              type="button"
              className="mission-member-locate" title="定位到地图位置"
              aria-label="定位"
              onClick={(e) => {
                e.stopPropagation();
                onLocate();
              }}
              style={{
                flexShrink: 0,
                width: 26,
                height: 26,
                padding: 0,
                borderRadius: 6,
                border: '1px solid #bfdbfe',
                background: '#eff6ff',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LocatePinIcon />
            </button>
          ) : null}

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!isStreaming) return;
              onOpenLive?.();
            }}
            title={isStreaming ? '观看前线直播' : '未直播'}
            className="mission-member-live" aria-label={isStreaming ? '观看直播' : '未直播'}
            style={{
              flexShrink: 0,
              height: 24,
              padding: '0 6px',
              borderRadius: 6,
              border: 'none',
              background: 'transparent',
              fontSize: 11,
              fontWeight: 600,
              cursor: isStreaming ? 'pointer' : 'default',
              color: isStreaming ? '#e53935' : '#9ca3af',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              lineHeight: 1
            }}
          >
            <CameraIcon active={!!isStreaming} />
            <span>{isStreaming ? '直播中' : '未直播'}</span>
          </button>

          {rightExtra}

          {onToggleExpand ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggleExpand();
                }
              }}
              title={expanded ? '收起' : '展开详情'}
              style={{
                fontSize: 12,
                cursor: 'pointer',
                color: '#6b7280',
                userSelect: 'none',
                display: 'inline-flex',
                width: 18,
                height: 22,
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                style={{ transform: expanded ? 'rotate(90deg)' : 'none' }}
              >
                <path
                  d="m9 5 7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          ) : null}
        </div>
      </div>

      {/* 第二行：相对时间 · 电量（截图样式）；轨迹滑块默认隐藏 */}
      <div
        className="mission-member-meta"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 4,
          minHeight: 18,
          paddingLeft: 14
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
            fontSize: 11,
            color: '#6b7280',
            whiteSpace: 'nowrap'
          }}
          title={metaTitle}
        >
          {metaLine ? (
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{metaLine}</span>
          ) : null}
        </div>
        {showCollapsedTrackSwitch && onTrackToggle ? (
          <TrackSwitch on={!!trackOn} onToggle={onTrackToggle} title={trackTitle} />
        ) : null}
      </div>
    </div>
  );
}



/** 相对更新文案：在线「N 秒前更新」；离线「最后更新 HH:MM」 */
export function formatCollapsedSeenText(lastSeen, { offline = false } = {}) {
  if (!lastSeen) return null;
  const d = new Date(lastSeen);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  const diffMs = Date.now() - d.getTime();
  if (offline || diffMs > 30 * 60 * 1000) {
    return `最后更新 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  const sec = Math.max(0, Math.floor(diffMs / 1000));
  if (sec < 60) return `${sec} 秒前更新`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分钟前更新`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小时前更新`;
  return `最后更新 ${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function pickReportIntervalSec(member) {
  const candidates = [
    member?.report_interval_sec,
    member?.reportIntervalSec,
    member?.report_interval,
    member?.reportInterval,
    member?.location_interval,
    member?.locationInterval,
    member?.refresh_interval,
    member?.refreshInterval,
    member?.track_interval,
    member?.trackInterval
  ];
  for (const c of candidates) {
    if (c == null || c === '') continue;
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return Math.round(n);
  }
  return null;
}

function resolveFreqMode(member, presenceStatus) {
  const st0 = String(presenceStatus || '').toLowerCase();
  const isOffline = st0 === 'offline' || presenceStatus === PRESENCE_STATUS.OFFLINE;
  if (isOffline) {
    return { kind: 'offline', label: '离线', bg: '#f3f4f6', color: '#6b7280' };
  }
  const mode = member?.track_mode
    || member?.location_mode
    || member?.refresh_mode
    || member?.report_mode
    || member?.reportMode
    || member?.freq_mode
    || member?.freqMode
    || null;
  const interval = pickReportIntervalSec(member);
  const raw = mode != null ? String(mode).toLowerCase() : '';
  if (raw.includes('boost') || raw.includes('high') || raw === 'realtime' || raw.includes('加强') || raw.includes('enhance')) {
    const label = interval != null ? `实时加强 · ${interval} 秒` : '实时加强';
    return { kind: 'boost', label, bg: '#dcfce7', color: '#15803d' };
  }
  if (raw.includes('pending') || raw.includes('wait') || raw.includes('待') || raw.includes('pending_boost')) {
    return { kind: 'pending', label: '等待生效', bg: '#fef3c7', color: '#c2410c' };
  }
  if (raw.includes('power') || raw.includes('save') || raw.includes('省电') || raw.includes('eco')) {
    const label = interval != null ? `省电 · ${interval} 秒` : '省电';
    return { kind: 'eco', label, bg: '#dbeafe', color: '#1d4ed8' };
  }
  if (raw.includes('offline')) {
    return { kind: 'offline', label: '离线', bg: '#f3f4f6', color: '#6b7280' };
  }
  // 无 mode、仅有上报间隔：短间隔视作加强，长间隔视作省电
  if (!raw && interval != null) {
    if (interval <= 15) {
      return { kind: 'boost', label: '实时加强 · ' + interval + ' 秒', bg: '#dcfce7', color: '#15803d' };
    }
    if (interval >= 45) {
      return { kind: 'eco', label: '省电 · ' + interval + ' 秒', bg: '#dbeafe', color: '#1d4ed8' };
    }
  }
  return null;
}

/**
 * 频率/策略徽标：绿加强 / 橙等待 / 蓝省电 / 灰离线。
 * 无后端字段时：离线显示「离线」；其它不渲染（避免假加强）。
 * 可用 member.__demoFreq 强制演示四种色态。
 */
export function FreqStatusBadge({ member, presenceStatus = null }) {
  let info = null;
  const demo = member?.__demoFreq || member?.demo_freq || null;
  if (demo) {
    const map = {
      boost: { kind: 'boost', label: '实时加强 · 10 秒', bg: '#dcfce7', color: '#15803d' },
      pending: { kind: 'pending', label: '等待生效', bg: '#fef3c7', color: '#c2410c' },
      eco: { kind: 'eco', label: '省电 · 60 秒', bg: '#dbeafe', color: '#1d4ed8' },
      offline: { kind: 'offline', label: '离线', bg: '#f3f4f6', color: '#6b7280' }
    };
    info = map[String(demo)] || null;
  } else {
    info = resolveFreqMode(member, presenceStatus);
  }
  if (!info) return null;
  return (
    <span
      title={info.label}
      style={{
        flexShrink: 0,
        fontSize: 10,
        fontWeight: 700,
        color: info.color,
        background: info.bg,
        borderRadius: 999,
        padding: '2px 8px',
        whiteSpace: 'nowrap',
        lineHeight: 1.4
      }}
    >
      {info.label}
    </span>
  );
}

export function summarizeFreqModes(members, getPresence) {
  const tallies = { boost: 0, pending: 0, eco: 0, offline: 0 };
  (members || []).forEach((m) => {
    const st = typeof getPresence === 'function' ? getPresence(m) : (m?.presence || null);
    const info = resolveFreqMode(m, st);
    if (!info) {
      const offline = String(st || '').toLowerCase() === 'offline' || st == null;
      if (offline) tallies.offline += 1;
      return;
    }
    if (info.kind === 'boost') tallies.boost += 1;
    else if (info.kind === 'pending') tallies.pending += 1;
    else if (info.kind === 'eco') tallies.eco += 1;
    else tallies.offline += 1;
  });
  return tallies;
}

export { TrackSwitch };

